import '@/plugins/globals'
import api from '@/plugins/api'
import eventBus from '@/plugins/eventBus'
import { EntityLoader } from '@/store/EntityLoader'
import fieldTranslation from '../../../.env.models.json'
import {
  generalSuccess,
  generalSuccessLogin,
  generalSuccessRegister,
  generalApiError,
  transferCreditsSuccess,
  generalError,
  confirmationPopup,
} from '@/plugins/popups'
import Model from 'scomponent/src/data/model'

declare const FB: any
declare const gapi: any

export class ClientModel extends Model {
  id: number = 0
  uuid: string = ''
  phone: string = ''
  mobilePhone: string = ''
  gender: string = ''

  constructor(data?: any, skipTranslation = false) {
    super()
    if (!data) {
      return
    }
    let translatorSecondObj: any = {}
    if (!process.env.VUE_APP_MODELS || skipTranslation) {
      return Object.assign(this, data)
    }
    if (fieldTranslation) {
      translatorSecondObj = fieldTranslation['User'].client
    }
    Object.keys(translatorSecondObj).forEach((localFieldName) => {
      Object.assign(this, {
        [localFieldName]: data[translatorSecondObj[localFieldName]],
      })
    })
  }
}

export class UserModel extends Model {
  private static instance: UserModel = new UserModel()
  id: number = 0
  uuid: string = ''
  firstName: string = ''
  lastName: string = ''
  email: string = ''
  mobileNumber: string = ''
  birthday: Date = new Date()
  password: string = ''
  client: ClientModel = new ClientModel()
  addresses: any

  public static getInstance(): UserModel {
    this.instance.setTranslateField(fieldTranslation['User'])
    return UserModel.instance
  }

  getFullName() {
    return `${this.firstName} ${this.lastName}`
  }

  saveToServer(successCallback: any, errorCallback: any) {
    let payload: object = {
      id: this.id,
      first_name: this.firstName,
      last_name: this.lastName,
      addresses: this.addresses,
      client: {
        mobile_phone: this.client.mobilePhone,
        phone: this.client.phone,
      },
      phone: this.mobileNumber,
      birthday: this.birthday,
    }
    if (!successCallback) {
      successCallback = () => {}
    }
    if (!errorCallback) {
      errorCallback = () => {}
    }

    api()
      .post(`clients/update`, payload)
      .then((response) => {
        if (!(response.data.status === 0)) {
          errorCallback()
          generalError()
          return
        }
        successCallback()
        generalSuccess()
      })
      .catch((error) => {
        errorCallback()
        generalError()
      })
  }

  public load(skipTranslation: boolean = false, callback: any): this {
    api()
      .get(`me`)
      .then((response) => {
        EntityLoader.assignDataToUser(this, response.data.data[0], skipTranslation)
        if (callback) {
          callback()
        }
      })
    return this
  }

  public assign(data: any, skipTranslation: boolean = false): this {
    EntityLoader.assignDataToUser(this, data, skipTranslation)
    return this
  }

  async loginWithCredentials(successCallback?: any, errorCallback?: any, email?: any, password?: any) {
    if (!successCallback) {
      successCallback = () => {}
    }
    if (!errorCallback) {
      errorCallback = () => {}
    }
    let payload = {
      provider: 'local',
      email: email ? email : this.email,
      password: password ? password : this.password,
      grant_type: 'password',
      client_id: process.env.VUE_APP_CLIENT_ID,
      client_secret: process.env.VUE_APP_CLIENT_SECRET,
      username: email ? email : this.email,
      scope: '*',
    }

    await api()
      .post('login', payload)
      .then((response) => {
        if (!(response.data.status === 0)) {
          errorCallback()
          return
        }
        api()
          .post('oauth/token', payload)
          .then((resultToken) => {
            this.assign(response.data.data[0])

            localStorage.setItem('user_data', JSON.stringify(this))
            localStorage.setItem('token', JSON.stringify(resultToken.data))
            eventBus.$emit('user:logged', this)
            if (!resultToken.data) {
              errorCallback()
              return generalError()
            } else if (typeof email !== 'string' || typeof password !== 'string') {
              generalSuccessLogin()
              successCallback()
              return
            }
          })
      })
      .catch((error) => {
        errorCallback()
        if (error.response) {
          return eventBus.$emit(`error`, error.response)
        }
        generalError()
      })
  }

  private loginApiCall(payload: any, successCallback?: any) {
    api()
      .post('login', payload)
      .then((response) => {
        if (!response.data) {
          return generalError()
        }

        if (typeof successCallback === 'function') {
          generalSuccessLogin()
        }

        localStorage.setItem('token', JSON.stringify(response.data.data[0].bearer.access_token))
        this.assign(response.data.data[0])
        localStorage.setItem('user_data', JSON.stringify(this))
        eventBus.$emit('user:logged', this)
      })
  }

  async loginWithFacebook(successCallback?: any, fbEmail?: any, password?: any) {
    if (fbEmail && password) {
      let payload: object = {
        email: fbEmail,
        access_token: password,
        password: password,
        provider: 'facebook',
        device_type: 'web',
      }
      return this.loginApiCall(payload)
    }

    await FB.login(
      (response: any) => {
        let accessToken = response.authResponse.accessToken
        let userId = response.authResponse.userID
        FB.api(`/${userId}?fields=id,email`, (response: any) => {
          let payload: object = {
            email: response.email,
            access_token: accessToken,
            password: accessToken,
            provider: 'facebook',
            device_type: 'web',
          }

          this.loginApiCall(payload, successCallback)
        })
      },
      { scope: 'public_profile, email' }
    )
  }

  loginWithGmail(successCallback?: any, gmEmail?: any, password?: any) {
    let googleAuth = gapi.auth2.getAuthInstance()

    if (gmEmail && password) {
      let payload: object = {
        email: gmEmail,
        access_token: password,
        provider: 'gmail',
        device_type: 'web',
      }

      return this.loginApiCall(payload)
    }

    googleAuth.then(() => {
      googleAuth.signIn({ scope: 'profile email' }).then((googleUser: any) => {
        let accessToken = googleUser.getAuthResponse().id_token
        let email = googleUser.getBasicProfile().getEmail()

        let payload: object = {
          email: email,
          access_token: accessToken,
          provider: 'gmail',
          device_type: 'web',
        }

        this.loginApiCall(payload, successCallback)
      })
    })
  }

  isLogged() {
    const token = localStorage.getItem('token')
    try {
      if (!token || token === '') {
        return false
      } else {
        const parsedToken = JSON.parse(token).access_token
        return parsedToken
      }
    } catch (error) {
      return false
    }
  }
  //Register

  register(payload: any, successCallback?: any, errorCallback?: any) {
    if (!successCallback) {
      successCallback = () => {}
    }
    if (!errorCallback) {
      errorCallback = () => {}
    }
    api()
      .post('register', payload)
      .then((response) => {
        if (response.status === 200) {
          successCallback()
          generalSuccessRegister()
          this.loginWithCredentials(errorCallback, successCallback, payload.email, payload.password)
          eventBus.$emit('user:register', response.status)
        }
        generalSuccessRegister()
      })
      .catch((error) => {
        errorCallback()
        generalApiError(error.response.data.errors[0])
        return eventBus.$emit(`error:register`, error.response)
      })
  }

  registerWithFacebook(successCallback?: any) {
    var self = this
    if (!successCallback) {
      successCallback = () => {}
    }

    FB.login(
      function (response: any) {
        let accessToken = response.authResponse.accessToken
        let userId = response.authResponse.userID

        FB.api(`/${userId}?fields=id,email,first_name,last_name`, 'get', (response: any) => {
          let payload: any = {
            email: response.email,
            access_token: accessToken,
            first_name: response.first_name,
            last_name: response.last_name,
            provider: 'facebook',
            device_type: 'web',
          }

          api()
            .post('register', payload)
            .then((response) => {
              if (response.status === 200) {
                generalSuccessRegister()
                self.loginWithFacebook(successCallback, payload.email, payload.access_token)
              }
            })
            .catch((error) => {
              if (error.response.data.status === 1000011) {
                alert('Ky Perdorues eshte i regjistruar, ju Lutem dilni nga adresa aktuale per tu loguar serisht.')
                return
              }
              generalError()
            })
        })
      },
      { scope: 'public_profile, email', return_scopes: true }
    )
  }

  registerWithGmail(successCallback?: any) {
    if (!successCallback) {
      successCallback = () => {}
    }
    let googleAuth = gapi.auth2.getAuthInstance()

    googleAuth.then(() => {
      googleAuth.signIn({ scope: 'profile email' }).then((googleUser: any) => {
        let accessToken = googleUser.getAuthResponse().id_token
        let email = googleUser.getBasicProfile().getEmail()
        let fullName = googleUser.getBasicProfile().getName()
        let firstName = fullName.split(' ').slice(0, -1).join(' ')
        let lastName = fullName.split(' ').slice(-1).join(' ')

        let payload: any = {
          email: email,
          first_name: firstName,
          last_name: lastName,
          provider: 'gmail',
          device_type: 'web',
          access_token: accessToken,
        }

        api()
          .post('register', payload)
          .then((response) => {
            if (response.status === 200) {
              generalSuccessRegister()
              this.loginWithGmail(successCallback, payload.email, payload.access_token)
            }
          })
          .catch((error) => {
            if (error.response.data.status === 1000010) {
              alert('Ky Perdorues eshte i regjistruar.')
              return
            }
            generalError()
          })
      })
    })
  }

  public transferCredits(payload: any) {
    confirmationPopup(() => {
      api()
        .post('me/transfer-credits', payload)
        .then((response) => {
          if (response.data.status === 0) {
            transferCreditsSuccess()
            eventBus.$emit('credits:transfered')
          }
        })
        .catch((error) => {
          if (error.response.status === 422) {
            return generalApiError(error.response.data.message)
          }
        })
    })
  }

  public loadFromStorage() {
    const userData = localStorage.getItem('user_data')
    if (!userData) {
      return this
    }
    try {
      let user = JSON.parse(userData)
      EntityLoader.assignDataToUser(this, user, false)

      this.id = +user.id
      this.firstName = user.firstName
      this.uuid = user.uuid
      this.firstName = user.firstName
      this.lastName = user.lastName
      this.email = user.email
      this.mobileNumber = user.mobileNumber
      this.birthday = user.birthday
    } catch (e) {
      console.log(e)
      // Ignore parsing errors and just remove the
    }
    return this
  }
}
